import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { validatePassword } from '@/lib/validations';
import { PasswordRequirements } from '@/components/ui/password-requirements';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    console.log('🔍 ResetPassword useEffect - token:', token);
    
    if (!token) {
      console.log('❌ No token found, redirecting to login');
      navigate('/login');
      return;
    }

    validateToken();
  }, [token, navigate]);

  const validateToken = async () => {
    try {
      setValidatingToken(true);
      console.log('🔍 Validating token via Edge Function:', token);
      
      const { data, error } = await supabase.functions.invoke('validate-reset-token', {
        body: { token }
      });

      console.log('🔍 Edge Function response:', { data, error });

      if (error) {
        console.error('🔍 Edge Function error:', error);
        throw new Error('Erro na validação do token');
      }

      if (!data?.valid) {
        const errorType = data?.error || 'unknown';
        console.log('🔍 Token validation failed:', errorType);
        
        if (errorType.includes('expired')) {
          throw new Error('Token expirado');
        } else if (errorType.includes('not found')) {
          throw new Error('Token não encontrado');
        } else {
          throw new Error('Token inválido');
        }
      }

      console.log('🔍 Token is valid, user:', data.user);
      setTokenValid(true);
    } catch (error: any) {
      console.error('🔍 Token validation error:', error);
      const isExpired = error.message?.includes('expirado');
      const isNotFound = error.message?.includes('não encontrado');
      
      let description = "O link de redefinição de senha é inválido ou expirou.";
      if (isExpired) {
        description = "O link de redefinição de senha expirou. Solicite um novo link.";
      } else if (isNotFound) {
        description = "Link de redefinição de senha não encontrado. Verifique se você copiou o link completo.";
      }

      toast({
        title: "Token Inválido",
        description,
        variant: "destructive",
      });
      navigate('/login');
    } finally {
      setValidatingToken(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem.",
        variant: "destructive",
      });
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      toast({
        title: "Senha não atende aos requisitos",
        description: passwordValidation.errors.join('. '),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Use edge function to update password securely
      const { data, error } = await supabase.functions.invoke('update-user-password', {
        body: {
          token: token,
          newPassword: formData.password
        }
      });

      if (error) {
        throw new Error(error.message || 'Falha ao atualizar senha');
      }

      if (!data?.success) {
        // Handle weak password error specifically
        if (data?.error_code === 'weak_password') {
          toast({
            title: "Senha Fraca",
            description: data.error || 'Esta senha foi encontrada em vazamentos de dados e não pode ser usada. Por favor, escolha uma senha mais forte e única.',
            variant: "destructive",
          });
          return;
        }
        throw new Error(data?.error || 'Falha ao atualizar senha');
      }

      toast({
        title: "Sucesso!",
        description: "Senha atualizada com sucesso. Você pode fazer login agora.",
        variant: "default",
      });

      navigate('/login');
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar senha. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Validando token...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Definir Nova Senha</CardTitle>
          <p className="text-muted-foreground">
            Por segurança, você deve definir uma nova senha para continuar.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={12}
                  placeholder="Digite sua nova senha (mín. 12 caracteres)"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  minLength={12}
                  placeholder="Confirme sua nova senha"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {formData.password && <PasswordRequirements password={formData.password} className="mt-4" />}

            <Alert>
              <AlertDescription>
                <strong>Dica de segurança:</strong>
                <p className="text-sm mt-1">
                  Evite usar senhas comuns ou dados pessoais. Esta senha nunca deve ter sido usada em outro site ou serviço.
                </p>
              </AlertDescription>
            </Alert>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !formData.password || !formData.confirmPassword}
            >
              {loading ? 'Atualizando...' : 'Definir Nova Senha'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}